import * as React from "react";
import { useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import GradingIcon from "@mui/icons-material/Grading";
import EditMapDetails from "./EditMapDetails";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  setOriginalParams,
  setSelectedDestinationTableIDMaps,
  setSelectedInterfaceIDMaps,
  setSourceFieldValidationData,
  setValidating,
} from "../../../../../features/mapsSlice";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import EditTestingProcessModal from "./EditTestingProcessModal";

const tableHeadings = {
  destination_table_column: "Destination Table Column",
  source_field_expression: "Source Field Expression",
  calculation_type: "Calculation Type ",
  unique_definition: "Unique Definition",
};
const rows = [
  {
    destination_table_column: "activity_date",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
  {
    destination_table_column: "user_unique_id",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
  {
    destination_table_column: "registrations",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
];

const EditMap = () => {
  const [searchParams] = useSearchParams();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const {
    mapDetails,
    interfaceData,
    destinationTablesData,
    isValidPayloadDetails,
    mapDetailsDataPayload,
  } = useSelector((state: any) => state.maps);
  const mapId: any = searchParams.get("mapId");
  const [navigatingBack] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedInterface, setSelectedInterface] = useState(null);

  const [selectedDestinationTable, setSelectedDestinationTable] =
    useState(null);
  const [mapNameInput, setMapNameInput] = React.useState("");
  const dispatch = useDispatch();
  const originalParams = useSelector((state: any) => state.maps.originalParams);
  const navigate = useNavigate();
  const fetchMapsData = async () => {
    setLoading(true);
    try {
      await MapsEndpointLogic.getMapDetails(oauthTokenHeader, mapId);
      await Promise.all([
        MapsEndpointLogic.getInterface(oauthTokenHeader),
        MapsEndpointLogic.getDestinationTables(oauthTokenHeader),
        MapsEndpointLogic.getAllCalculationTypesMaps(oauthTokenHeader),
        MapsEndpointLogic.getUnifierUniqueDefinitionMaps(oauthTokenHeader),
      ]);
    } catch (error: any) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.parsedText ||
            "Error while fetching data. Please try again later.",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    fetchMapsData();

    // eslint-disable-next-line
  }, []);

  const getValidationPayload = () => {
    return {
      field_mapping: mapDetailsDataPayload.map((item: any) => ({
        field_mapping_id: item.field_mapping_id,
        source_field_expression: item.source_field_expression,
      })),
    };
  };

  const handleTestChanges = async () => {
    const payload = getValidationPayload();
    dispatch(setValidating(true));
    showTestingProcessDialog();
    try {
      await MapsEndpointLogic.validateSourceField(oauthTokenHeader, payload);
      // optionally open modal after success
    } catch (error) {
      console.error("Validation failed:", error);
      // Optionally dispatch an error snackbar here if you want
    } finally {
      dispatch(setValidating(false));
    }
  };
  const selectedInterfaceValue =
    selectedInterface &&
    interfaceData?.find(
      (item: any) => item.affiliate_system_interface_name === selectedInterface
    )
      ? {
          label: selectedInterface,
          id: interfaceData.find(
            (item: any) =>
              item.affiliate_system_interface_name === selectedInterface
          ).id,
        }
      : null;

  const selectedDestinationTableValue =
    selectedDestinationTable &&
    destinationTablesData?.find(
      (item: any) => item.destination_table === selectedDestinationTable
    )
      ? {
          label: selectedDestinationTable,
          id: destinationTablesData.find(
            (item: any) => item.destination_table === selectedDestinationTable
          ).id,
        }
      : null;
  // Prefill interface & destination table from mapDetails on load
  React.useEffect(() => {
    if (mapDetails?.map_name) {
      setMapNameInput(mapDetails.map_name); // optional suffix for duplicate
    }
    if (mapDetails?.backend_interface_id && interfaceData?.length > 0) {
      const matchedInterface = interfaceData.find(
        (item: any) => item.id === mapDetails.backend_interface_id
      );
      if (matchedInterface) {
        setSelectedInterface(matchedInterface.affiliate_system_interface_name);
        dispatch(setSelectedInterfaceIDMaps(matchedInterface.id));
      }
    }

    if (mapDetails?.destination_table_id && destinationTablesData?.length > 0) {
      const matchedDestTable = destinationTablesData.find(
        (item: any) => item.id === mapDetails.destination_table_id
      );
      if (matchedDestTable) {
        setSelectedDestinationTable(matchedDestTable.destination_table);
        dispatch(setSelectedDestinationTableIDMaps(matchedDestTable.id));
      }
    }
  }, [mapDetails, interfaceData, destinationTablesData, dispatch]);

  React.useEffect(() => {
    return () => {
      if (!navigatingBack) return;
      if (originalParams) {
        navigate(`/data_collection/unifier/maps?${originalParams}`, {
          replace: true,
        });
      } else {
        navigate(`/data_collection/unifier/maps`, { replace: true });
      }
      dispatch(setOriginalParams(""));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openModal, setOpenModal] = React.useState(false);

  const showTestingProcessDialog = () => {
    setOpenModal(true);
    dispatch(setSourceFieldValidationData({}));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Paper elevation={0} sx={{ padding: "12px 16px", mb: "16px" }}>
            <Grid container alignItems="center" columnSpacing={3}>
              <Grid size={12} mb={2}>
                <Typography variant="h3" sx={{ fontSize: "24px" }}>
                  Edit Map
                </Typography>
              </Grid>
              <Grid size={9}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent={"flex-end"}
                  gap={2}
                >
                  <TextField
                    required
                    label="Map Name" // Set your label here
                    placeholder="Please enter map name" // Set your placeholder here
                    color="secondary"
                    size="small"
                    fullWidth
                    value={mapNameInput}
                    onChange={(e) => setMapNameInput(e.target.value)}
                    onBlur={() =>
                      setMapNameInput((prev) => (prev ? prev.trim() : prev))
                    }
                  />
                  <Autocomplete
                    sx={{ width: "100%" }}
                    value={selectedInterfaceValue}
                    disabled
                    options={
                      interfaceData?.map((item: any) => ({
                        label: item.affiliate_system_interface_name,
                        id: item.id,
                      })) || []
                    }
                    onChange={(_event, newValue: any) => {
                      setSelectedInterface(newValue?.label);
                      dispatch(setSelectedInterfaceIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />

                  <Autocomplete
                    sx={{ width: "100%" }}
                    value={selectedDestinationTableValue}
                    disabled
                    options={
                      destinationTablesData?.map((item: any) => ({
                        label: item.destination_table,
                        id: item.id,
                      })) || []
                    }
                    onChange={(_event, newValue: any) => {
                      setSelectedDestinationTable(newValue?.label);
                      dispatch(setSelectedDestinationTableIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Destination Table"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                </Stack>
              </Grid>
              <Grid size={3} textAlign={"right"}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    !mapNameInput ||
                    // !selectedInterfaceValue ||
                    // !selectedDestinationTableValue ||
                    !isValidPayloadDetails
                  }
                  onClick={handleTestChanges}
                  sx={{
                    padding: "8px 20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Test CHANGES
                  <GradingIcon sx={{ width: "20px", ml: "5px" }} />
                  {/* {isEditing && <SimpleBackdrop />} */}
                  {/* <SaveOutlinedIcon sx={{ width: "20px", ml: "5px" }} /> */}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Edit Map Details Table*/}
          <EditMapDetails
            tableHeadings={tableHeadings}
            rows={rows}
            selectedInterface={selectedInterface}
            selectedDestinationTable={selectedDestinationTable}
          />

          {/* Edit Testing Process Modal*/}
          <EditTestingProcessModal
            open={openModal}
            handleClose={handleCloseModal}
            mapNameInput={mapNameInput}
            setMapNameInput={setMapNameInput}
          />
        </>
      )}
    </>
  );
};

export default EditMap;
